/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconArrowLeft from '@apollo/icons/small/IconArrowLeft.svg';
import IconArrowRight from '@apollo/icons/small/IconArrowRight.svg';
import IconBookmark from '@apollo/icons/small/IconBookmark.svg';
import IconClose from '@apollo/icons/small/IconClose.svg';
import IconLock from '@apollo/icons/small/IconLock.svg';
import { Button, IconButton, Spinner } from '@apollo/orbit';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import React from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TruncateWithTooltip } from 'src/components/TruncateWithTooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { graphRefToString } from '../../hooks/useGraphRef';
import { SavedOperationLocalStateEntry } from '../hooks/useExplorerState/useEditorTabState/useSavedOperationLocalState';

export const Tab = ({
  operationId,
  isSelected,
  setSelectedTab,
  savedOperationLocalStateEntry,
  closeTab,
  unsavedDisplayName,
  isBeingDragged,
  savedEntryDetails,
}: {
  operationId: string;
  isSelected: boolean;
  setSelectedTab: (tabId: string) => void;
  savedOperationLocalStateEntry: SavedOperationLocalStateEntry | undefined;

  closeTab: (tabId: string) => void;
  unsavedDisplayName: string;
  isBeingDragged: boolean;
  savedEntryDetails:
    | {
        name: string;
        canEditSavedEntryCollection: boolean;
        collection: GraphQLTypes.GetCollectionEntries_operationCollectionEntries_collection;
      }
    | undefined;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: operationId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const canEditSavedEntryCollection =
    savedEntryDetails?.canEditSavedEntryCollection;
  const tabName = savedEntryDetails?.name ?? unsavedDisplayName;

  const isModified =
    savedOperationLocalStateEntry?.operationModified ||
    savedOperationLocalStateEntry?.variablesModified ||
    savedOperationLocalStateEntry?.headersModified ||
    savedOperationLocalStateEntry?.preflightOperationScriptModified ||
    savedOperationLocalStateEntry?.postflightOperationScriptModified;

  const collection = savedEntryDetails?.collection;

  const savedEntryFullPaths = (
    collection?.isSandbox
      ? ['sandbox']
      : collection?.variants?.map((v) =>
          graphRefToString({
            graphId: v.graphId,
            graphVariant: v.name,
          }),
        )
  )
    ?.map(
      (v) =>
        `${v}/${collection?.isShared ? 'shared' : 'personal'}/${
          collection?.name
        }/${savedEntryDetails?.name}`,
    )
    .join(', ');

  const tabNameContent = (
    <div
      className={classnames(
        'flex-1 overflow-hidden truncate text-left',
        isSelected ? 'text-primary' : 'text-secondary',
        isModified && 'italic',
      )}
    >
      {savedOperationLocalStateEntry ? (
        !savedEntryDetails ? (
          <>
            Loading
            <Spinner size="xs" className="ml-1" />
          </>
        ) : (
          <Tooltip label={savedEntryFullPaths}>
            <span>{savedEntryDetails.name}</span>
          </Tooltip>
        )
      ) : (
        <TruncateWithTooltip content={unsavedDisplayName} />
      )}
    </div>
  );

  return (
    <li
      style={style}
      key={operationId}
      // NOTE: when changing the width / height or padding of this tab, also change the DragOverlay in ExplorerTabs.tsx
      className="group relative"
    >
      <Button
        ref={setNodeRef}
        variant="hidden"
        className={classnames(
          /**
           * We need lots of custom classes here so that the Button mimics the look of Tab.
           * It would be nice to update Explorer to use Tab straight from Orbit, but Tabs in Orbit
           * are controlled and tabs in Explorer are also highly controlled, so this would take
           * some engineering focus to get right.
           */
          'rounded-none border-b-4 bg-transparent px-1 hover:bg-transparent active:bg-transparent',
          isSelected
            ? 'border-tab'
            : 'border-transparent font-normal hover:border-tab',
        )}
        css={css`
          /* this class is used to hide arrow indicators when using the mouse (regular focus), but show them when using keyboard (focus-visible) */
          .arrow-indicator {
            display: none;
          }
          &:focus-visible {
            > .arrow-indicator {
              display: block;
            }
          }
        `}
        onClick={() => {
          // This doesn't seem to actually ever fire; It's eclipsed by the drag-and-drop dragStart and dragEnd
          setSelectedTab(operationId);
        }}
        {...attributes}
        {...listeners}
        role="tab"
        leftIcon={
          savedOperationLocalStateEntry !== undefined ? (
            canEditSavedEntryCollection !== false ? (
              <IconBookmark />
            ) : (
              <Tooltip label="This operation is read-only. You can still edit it locally, but changes cannot be saved and shared with other members of this org.">
                <IconLock />
              </Tooltip>
            )
          ) : undefined
        }
        rightIcon={
          isModified ? (
            <div className="flex size-3 items-center justify-center">
              <div
                className="size-2 rounded-full bg-brand-primary"
                data-testid="modified_operation"
              />
            </div>
          ) : (
            // placeholder for Close button, which can't be nested in this button
            <div className="size-3" />
          )
        }
      >
        {isBeingDragged && <IconArrowLeft className="arrow-indicator mr-2" />}
        <span className="flex-1">{tabNameContent}</span>
        {isBeingDragged && <IconArrowRight className="arrow-indicator ml-2" />}
      </Button>
      <IconButton
        variant={isModified ? 'secondary' : 'hidden'}
        className={classnames(
          'absolute right-0 top-1.5',
          isModified && 'invisible group-hover:visible',
        )}
        size="xs"
        onClick={() => closeTab(operationId)}
        icon={<IconClose />}
        aria-label={`Close tab '${tabName}'`}
      />
    </li>
  );
};
